
      const b64 = (s) => {
        const pad = "=".repeat((4 - (s.length % 4)) % 4)
        const raw = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"))
        return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)))
      }

      const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent)

      // A Home Screen web app gets its own window object and reports the
      // standalone display mode. A Safari tab exposes no PushManager at
      // all on iOS, which is what "This browser cannot take push alerts"
      // meant — the prompt cannot be asked there, so explain before the tap.
      const installed = () =>
        window.navigator.standalone === true ||
        (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches)

      export default {
        mounted() {
          this.enable = this.el.querySelector("#push-enable")
          this.disable = this.el.querySelector("#push-disable")
          this.hint = this.el.querySelector("#push-hint")
          this.vapid = this.el.dataset.vapidPublic
          this.enable?.addEventListener("click", () => this.subscribe())
          this.disable?.addEventListener("click", () => this.unsubscribe())

          this.applyUnsupported()
        },
        updated() {
          // A tab switch re-renders this section, and LiveView restores the
          // template's class/value on the hint and the button. Re-apply.
          this.applyUnsupported()
        },
        applyUnsupported() {
          if (!this.enable) return
          if (!this.supported()) {
            this.setHint(this.unsupportedHint())
            this.enable.disabled = true
            return
          }
          this.enable.disabled = false
          if (typeof Notification !== "undefined" && Notification.permission === "denied") {
            this.setHint(this.blockedHint())
          }
        },
        supported() {
          // `"PushManager" in window` is true for a property that exists
          // but is undefined. iOS in a Safari tab behaves that way.
          return "serviceWorker" in navigator && typeof window.PushManager === "function"
        },
        unsupportedHint() {
          if (isIOS() && !installed()) {
            return "On iPhone, notifications work only after you add icons4good to your Home Screen: Share → Add to Home Screen, then open it from that icon."
          }
          return "This browser cannot take push alerts. Try Safari on iPhone (from the Home Screen icon) or Chrome on Android."
        },
        blockedHint() {
          return "Notifications are blocked for this site. Allow them in the browser or phone settings, reload this page, then tap again."
        },
        setHint(text) {
          if (!this.hint) return
          this.hint.textContent = text || ""
          this.hint.classList.toggle("hidden", !text)
        },
        async registration() {
          if (!this.supported()) {
            this.setHint(this.unsupportedHint())
            return null
          }
          return navigator.serviceWorker.register("/sw.js")
        },
        async subscribe() {
          const reg = await this.registration()
          if (!reg) return
          try {
            if (typeof Notification === "undefined") {
              this.setHint(this.unsupportedHint())
              return
            }
            // A blocked grant cannot be lifted from the page. Asking again
            // is a silent no-op; the only recovery is OS/browser settings.
            let permission = Notification.permission
            if (permission === "denied") {
              this.setHint(this.blockedHint())
              return
            }
            if (permission !== "granted") {
              permission = await Notification.requestPermission()
            }
            if (permission !== "granted") {
              this.setHint(
                Notification.permission === "denied"
                  ? this.blockedHint()
                  : "The browser did not grant permission. Check the lock icon in the address bar, or allow notifications in settings, then reload."
              )
              return
            }
            this.setHint("")
            const sub = await reg.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: b64(this.vapid),
            })
            const json = sub.toJSON()
            this.pushEvent("push_subscribe", {
              endpoint: json.endpoint,
              p256dh: json.keys.p256dh,
              auth: json.keys.auth,
              user_agent: navigator.userAgent,
            })
          } catch (err) {
            const ios = /iPhone|iPad|iPod/.test(navigator.userAgent)
            this.setHint(
              ios
                ? "On iPhone, add icons4good to your Home Screen, then open it from there."
                : "Could not turn notifications on."
            )
          }
        },
        async unsubscribe() {
          const reg = await this.registration()
          if (!reg) return
          const sub = await reg.pushManager.getSubscription()
          const endpoint = sub ? sub.endpoint : ""
          if (sub) await sub.unsubscribe()
          this.pushEvent("push_unsubscribe", {endpoint})
        }
      }
    